import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { motion } from 'framer-motion';

export const Route = createFileRoute('/signup')({
  component: SignupPage,
});

function SignupPage() {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    businessName: '',
    businessNameEn: '',
    email: '',
    password: '',
    phone: '',
    city: '',
    slug: '',
  });
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  function updateField(key: keyof typeof form, value: string) {
    if (key === 'phone') {
      value = value.replace(/[^\d+]/g, '');
    }
    setForm((f) => ({ ...f, [key]: value }));
    if (key === 'businessNameEn') {
      const auto = value.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').slice(0, 30);
      setForm((f) => ({ ...f, slug: auto }));
      if (auto.length >= 3) void checkSlug(auto);
    }
    if (key === 'slug') {
      const clean = value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30);
      setForm((f) => ({ ...f, slug: clean }));
      if (clean.length >= 3) void checkSlug(clean);
    }
  }

  async function checkSlug(slug: string) {
    try {
      const res = await fetch(`/api/auth/check-slug/${encodeURIComponent(slug)}`);
      const data = (await res.json()) as { available: boolean };
      setSlugAvailable(data.available);
    } catch {
      setSlugAvailable(null);
    }
  }

  async function submit() {
    setBusy(true);
    setError('');
    try {
      const res = await fetch('/api/auth/signup', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(form),
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? 'فشل إنشاء الحساب');
      }
      const data = (await res.json()) as { slug: string };
      // Redirect to the new tenant's admin (which auto-redirects to onboarding)
      const { protocol, hostname, port } = window.location;
      if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
        window.location.href = `${protocol}//${data.slug}.localhost:${port}/admin?welcome=1`;
      } else {
        const parts = hostname.split('.');
        const domain = parts.slice(-2).join('.');
        window.location.href = `${protocol}//${data.slug}.${domain}/admin?welcome=1`;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'فشل إنشاء الحساب');
    } finally {
      setBusy(false);
    }
  }

  const steps = [
    // Step 0: Business info
    <motion.div key="biz" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
      <h2 className="text-xl font-black text-oud-900">معلومات النشاط</h2>
      <Field label="اسم النشاط (عربي)" value={form.businessName} onChange={(v) => updateField('businessName', v)} placeholder="مثال: ورشة الخشب" />
      <Field label="اسم النشاط (إنجليزي)" value={form.businessNameEn} onChange={(v) => updateField('businessNameEn', v)} placeholder="e.g. Al Khashab Workshop" />
      <div>
        <Field label="رابط المتجر" value={form.slug} onChange={(v) => updateField('slug', v)} placeholder="al-khashab" />
        {form.slug.length >= 3 ? (
          <p className={`text-xs mt-1 ${slugAvailable === true ? 'text-green-700' : slugAvailable === false ? 'text-red-700' : 'text-oud-500'}`}>
            {form.slug}.sindian.app — {slugAvailable === true ? 'متاح ✓' : slugAvailable === false ? 'غير متاح' : 'جارٍ التحقق…'}
          </p>
        ) : null}
      </div>
      <button type="button" onClick={() => setStep(1)} disabled={!form.businessName || !form.businessNameEn || !form.slug || slugAvailable !== true}
        className="w-full bg-oud-800 text-sand rounded-2xl py-3 font-bold disabled:opacity-40 hover:bg-oud-900 transition-colors">
        التالي
      </button>
    </motion.div>,

    // Step 1: Contact + auth
    <motion.div key="contact" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
      <h2 className="text-xl font-black text-oud-900">بيانات الحساب</h2>
      <Field label="البريد الإلكتروني" value={form.email} onChange={(v) => updateField('email', v)} placeholder="email@example.com" type="email" />
      <Field label="كلمة المرور" value={form.password} onChange={(v) => updateField('password', v)} placeholder="٦ أحرف على الأقل" type="password" />
      <div>
        <Field label="رقم الجوال" value={form.phone} onChange={(v) => updateField('phone', v)} placeholder="05xxxxxxxx" />
        {form.phone && !/^(\+?966|0)5\d{8}$/.test(form.phone) ? (
          <p className="text-xs text-red-700 mt-1">رقم جوال سعودي غير صحيح (مثال: 05xxxxxxxx)</p>
        ) : null}
      </div>
      <Field label="المدينة (اختياري)" value={form.city} onChange={(v) => updateField('city', v)} placeholder="الدمام، الرياض…" />
      {error ? <p className="text-sm text-red-700">{error}</p> : null}
      <div className="flex gap-3">
        <button type="button" onClick={() => setStep(0)} className="flex-1 bg-cream border border-oud-100 text-oud-700 rounded-2xl py-3 font-bold hover:bg-oud-50 transition-colors">
          رجوع
        </button>
        <button type="button" onClick={submit} disabled={busy || !form.email || form.password.length < 6 || !/^(\+?966|0)5\d{8}$/.test(form.phone)}
          className="flex-1 bg-oud-800 text-sand rounded-2xl py-3 font-bold disabled:opacity-40 hover:bg-oud-900 transition-colors">
          {busy ? 'جارٍ الإنشاء…' : 'إنشاء حساب'}
        </button>
      </div>
    </motion.div>,
  ];

  return (
    <div className="min-h-screen bg-sand text-oud-900 flex items-center justify-center p-6" dir="rtl">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <div className="flex items-center justify-between">
            <span />
            <h1 className="text-3xl font-black">سنديان</h1>
            <a href="/" className="text-sm text-oud-500 hover:text-oud-900 font-bold flex items-center gap-1">
              <span>رجوع</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5" aria-hidden><path d="M15 6l-6 6 6 6" /></svg>
            </a>
          </div>
          <p className="text-oud-600 text-sm">سجّل نشاطك وابدأ خلال دقائق</p>
        </div>

        {/* Progress */}
        <div className="flex gap-2">
          {[0, 1].map((i) => (
            <div key={i} className={`flex-1 h-1.5 rounded-full transition-colors ${i <= step ? 'bg-gold' : 'bg-oud-100'}`} />
          ))}
        </div>

        {steps[step]}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', dir }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string; dir?: string;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-bold text-oud-800">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        dir={dir}
        className="w-full bg-cream border border-oud-100 rounded-xl px-4 py-3 text-base text-oud-900 placeholder-oud-300 focus:outline-none focus:border-gold"
      />
    </label>
  );
}
